import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";

const CitizenDistributionTable = () => {
  const pid = useSelector((state) => state.user.projectId);

  // ================= FILTER STATES =================
  const [orgOptions, setOrgOptions] = useState([]);
  const [caseDistrictOptions, setCaseDistrictOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedCaseDistrict, setSelectedCaseDistrict] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    caseDistricts: [],
    startDate: "",
    endDate: ""
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions with examples
  const definitions = {
    "Date Range": {
      title: "Date Range",
      description: "The date range filter allows you to select a specific period to analyze work done. Both the start date and end date are inclusive in the query.",
      example: "If you select 1/Jan/2026 - 31/Mar/2026, the data will include work done on 1st January AND 31st March as well."
    },
    "Organization": {
      title: "Organization",
      description: "Choose the Org or Workspace name, which corresponds to the state or workspace associated with the selected Project.",
      example: "Example: Maharashtra, Karnataka, Delhi NCR, etc."
    },
    "Service District": {
      title: "Service District",
      description: "Filter data by the district where services are being provided. This helps in analyzing location-specific performance.",
      example: "Example: Gurugram, Bangalore, Delhi, Chennai, etc."
    },
    "Unique Citizen Distribution": {
      title: "Unique Citizen Distribution",
      description: "This table shows the distribution of unique citizens across different application statuses. Each citizen is counted only once per status, even if they have multiple applications.",
      example: "• Applications Opened: 100 unique citizens who opened applications\n• Applications Submitted: 75 unique citizens who submitted applications\n• Applications with Benefit Received: 60 unique citizens who received benefits"
    },
    "Status": {
      title: "Status",
      description: "The current stage of the application in the workflow process.",
      example: "Applications Opened → Applications Submitted → Applications with Benefit Received"
    },
    "Unique Citizens": {
      title: "Unique Citizens",
      description: "The count of distinct citizens in each status category. A citizen is counted only once per status, even if they have multiple applications in that status.",
      example: "If a citizen has 3 applications in 'Submitted' status, they will be counted only once in Unique Citizens."
    },
    "Applications": {
      title: "Applications",
      description: "The total number of applications in each status category. This counts each application individually, including multiple applications from the same citizen.",
      example: "If a citizen has 3 applications in 'Submitted' status, all 3 will be counted in Applications."
    }
  };

  // ================= QUERY PARAMS =================
  const getQueryParams = () => {
    const params = new URLSearchParams();

    if (pid) params.append("pid", pid);

    appliedFilters.orgs.forEach(o => params.append("OrgName", o));
    appliedFilters.caseDistricts.forEach(d => params.append("CaseDistrict", d));

    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);

    return params.toString();
  };

  // ================= FETCH FILTER OPTIONS =================
  const fetchFilters = async () => {
    try {
      const params = getQueryParams();

      const [orgRes, districtRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-case-districts/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const districtData = await districtRes.json();

      setOrgOptions(
        orgData.unique_Organizations.map(o => ({ value: o, label: o }))
      );

      setCaseDistrictOptions(
        districtData.unique_case_districts.map(d => ({ value: d, label: d }))
      );

    } catch (err) {
      console.error("Filter fetch error:", err);
    }
  };

  // ================= FETCH TABLE DATA =================
  const fetchData = async () => {
    setLoading(true);

    try {
      const params = getQueryParams();

      const [funnelRes, citizenRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/funnel/chart/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/unique-citizen-distribution/?${params}`)
      ]);

      const funnel = await funnelRes.json();
      const citizen = await citizenRes.json();

      const tableData = [
        {
          status: "Applications Opened",
          cases: funnel.application_open || 0,
          citizens: funnel.citizen_open_count || 0,
          newly: citizen.newly_citizen_open_count || 0
        },
        {
          status: "Applications Submitted",
          cases: funnel.docket_submitted || 0,
          citizens: funnel.citizen_ds_count || 0,
          newly: citizen.newly_citizen_ds_count || 0
        },
        {
          status: "Applications with Benefit Received",
          cases: funnel.br_count || 0,
          citizens: funnel.citizen_br_count || 0,
          newly: citizen.newly_citizen_br_count || 0
        }
      ];

      setData(tableData);

    } catch (err) {
      console.error("Data fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= APPLY FILTERS =================
  const applyFilters = () => {
    setAppliedFilters({
      orgs: selectedOrgs,
      caseDistricts: selectedCaseDistrict,
      startDate,
      endDate
    });
  };

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedCaseDistrict([]);
    setStartDate("");
    setEndDate("");

    setAppliedFilters({
      orgs: [],
      caseDistricts: [],
      startDate: "",
      endDate: ""
    });
  };

  useEffect(() => {
    fetchFilters();
  }, [appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [appliedFilters, pid]);

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />

        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  const MultiSelect = ({ options, value, setValue, placeholder, label }) => (
    <div>
      <div className="flex items-center mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <InfoButton title={label} />
      </div>
      <Select
        isMulti
        options={options}
        value={options.filter(o => value.includes(o.value))}
        onChange={(val) => setValue(val.map(v => v.value))}
        placeholder={placeholder}
        className="text-sm"
        styles={{
          control: (base) => ({
            ...base,
            borderColor: '#e2e8f0',
            '&:hover': { borderColor: '#94a3b8' },
            boxShadow: 'none',
            '&:focus-within': {
              borderColor: '#3b82f6',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }
          })
        }}
      />
    </div>
  );

  return (
    <div className="max-w-[90vw] ml-[3vw] mt-[1vh] border border-gray-200 shadow-lg rounded-xl bg-white">

      {/* HEADER */}
      <div className="px-6 py-4 rounded-t-xl border-b border-gray-200">
        <div className="flex items-center">
          <h2 className="text-xl font-bold text-pink-800">Unique Citizen Distribution</h2>
          <InfoButton title="Unique Citizen Distribution" />
        </div>
        <p className="text-sm text-pink-600 mt-1">
          Track unique citizens across different application statuses
        </p>
      </div>

      {/* FILTERS */}
      <div className="px-6 py-5 bg-white border-b border-gray-200">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          <MultiSelect
            options={orgOptions}
            value={selectedOrgs}
            setValue={setSelectedOrgs}
            placeholder="Select org/workspace"
            label="Org/Workspace"
          />

          <MultiSelect
            options={caseDistrictOptions}
            value={selectedCaseDistrict}
            setValue={setSelectedCaseDistrict}
            placeholder="Select district..."
            label="Service District"
          />

          <div className="md:col-span-2">
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">Date Range</label>
              <InfoButton title="Date Range" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm w-full"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={e => setEndDate(e.target.value)}
                className="border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm w-full"
                placeholder="End Date"
              />
            </div>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={applyFilters}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-all duration-200"
          >
            Clear Filters
          </button>
        </div>

      </div>

      {/* TABLE */}
      <div className="p-6 bg-gray-50/30">

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-gray-500">Loading data...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-3 text-left">
                    <div className="flex items-center">
                      <span>Status</span>
                      <InfoButton title="Status" />
                    </div>
                  </th>
                  <th className="border p-3 text-left">
                    <div className="flex items-center">
                      <span>Unique Citizens</span>
                      <InfoButton title="Unique Citizens" />
                    </div>
                  </th>
                  <th className="border p-3 text-left">
                    <div className="flex items-center">
                      <span>Applications</span>
                      <InfoButton title="Applications" />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="border p-3 font-semibold text-gray-800">{row.status}</td>
                    <td className="border p-3 text-green-600 font-bold">
                      {row.newly.toLocaleString()}
                    </td>
                    <td className="border p-3 text-gray-700">{row.cases.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>

    </div>
  );
};

export default CitizenDistributionTable;